import { useState, useEffect, useMemo, useCallback } from "react";
import AppShell from "@/components/AppShell";
import SimulatedBadge from "@/components/paper/SimulatedBadge";
import PageHeader from "@/components/PageHeader";
import PnLBreakdownTree from "@/components/PnLBreakdownTree";
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, X, ArrowRight } from "lucide-react";

const API = (p) => `/api${p}`;

const fmt = (n) =>
  n == null ? "—" : `${n >= 0 ? "+" : ""}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const fmtPct = (n) => n == null ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

// RU-PNLFIX PF-6/PF-7: what each figure is. Combined keeps the snapshot's equity change and says so;
// the realized paper P&L is shown alongside. Every P&L is gross (charges are not modelled yet).
export const COMBINED_LABEL = "Equity change (incl. open & broker book)";
export const REALIZED_LABEL = "Realized";
export const BEFORE_CHARGES = "before charges";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS  = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function buildCalendarGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function dayColor(pnl) {
  if (pnl == null) return { bg: "bg-transparent", text: "text-zinc-600", border: "border-zinc-800/40" };
  if (pnl > 0)     return { bg: "bg-emerald-950/60", text: "text-emerald-400", border: "border-emerald-800/50" };
  if (pnl < 0)     return { bg: "bg-red-950/60",     text: "text-red-400",     border: "border-red-900/50" };
  return { bg: "bg-zinc-900", text: "text-zinc-500", border: "border-zinc-800" };
}

function intensity(pnl, maxAbs) {
  if (!pnl || !maxAbs) return 0;
  return Math.min(Math.abs(pnl) / maxAbs, 1);
}

// ── Day Drilldown (inline) ─────────────────────────────────────────────────────

function TradeRow({ trade }) {
  const win = trade.pnl > 0;
  const lose = trade.pnl < 0;
  const label = trade.instrument_type === "OPTION"
    ? `${trade.symbol} ${trade.option_strike}${trade.option_kind}`
    : trade.symbol;

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-zinc-800/60 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono font-medium text-zinc-200">{label}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${
            trade.direction === "BUY" ? "bg-emerald-900/60 text-emerald-400" : "bg-red-900/60 text-red-400"
          }`}>{trade.direction}</span>
          {trade.mode && <span className="text-[9px] text-zinc-600 uppercase">{trade.mode}</span>}
          {trade.simulated ? <SimulatedBadge /> : null}
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-500 font-mono flex-wrap">
          <span>Qty {trade.qty}</span>
          <span className="text-zinc-700">·</span>
          <span>₹{trade.entry_price?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          <ArrowRight className="w-2.5 h-2.5 text-zinc-700" />
          <span>₹{trade.exit_price?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          <span className="text-zinc-700">·</span>
          <span>{fmtTime(trade.entry_at)} → {fmtTime(trade.exit_at)}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-sm font-mono font-semibold ${win ? "text-emerald-400" : lose ? "text-red-400" : "text-zinc-400"}`}>
          {fmt(trade.pnl)}
        </div>
        <div className={`text-[10px] font-mono ${win ? "text-emerald-500/70" : lose ? "text-red-500/70" : "text-zinc-600"}`}>
          {fmtPct(trade.pnl_pct)}
        </div>
      </div>
    </div>
  );
}

export function DayDetail({ date, mode = "paper", onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setDetail(null);
    fetch(API(`/analytics/day?date=${date}`), { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setDetail(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);

  const totalPnl = detail?.summary?.total_pnl ?? 0;
  // PF-10: the day total is always the paper ledger; outside Paper mode say so.
  const totalLabel = `${mode === "paper" ? "Realized P&L" : "Paper realized P&L"} (${BEFORE_CHARGES})`;
  const isProfit = totalPnl > 0;
  const isLoss   = totalPnl < 0;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Day Detail</div>
          <div className="text-sm font-semibold text-zinc-100">{date}</div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-zinc-600 text-sm animate-pulse">
          Loading positions…
        </div>
      )}

      {!loading && detail && (
        <>
          {/* PF-9: more rows than the cap is reported, never silently cut (same warning as the breakdown) */}
          {detail.truncated && (
            <div className="mx-5 mt-4 border border-red-900/60 bg-red-950/30 rounded-sm px-3 py-2 text-[11px] text-red-300" data-testid="day-truncated-warning">
              More than {Number(detail.row_cap).toLocaleString("en-IN")} trades or orders around this day: the list and totals cover only the first{" "}
              {Number(detail.row_cap).toLocaleString("en-IN")}. Figures for this day may be incomplete.
            </div>
          )}
          {/* Summary strip */}
          <div className="grid grid-cols-4 gap-px bg-zinc-800 border-b border-zinc-800">
            {[
              { label: totalLabel, value: fmt(detail.summary.total_pnl), color: isProfit ? "text-emerald-400" : isLoss ? "text-red-400" : "text-zinc-400", testid: "day-total" },
              { label: "Trades",     value: detail.summary.closed_trades,         color: "text-zinc-200" },
              { label: "Wins",       value: detail.summary.winning_trades,        color: "text-emerald-400" },
              { label: "Losses",     value: detail.summary.losing_trades,         color: "text-red-400" },
            ].map(({ label, value, color, testid }) => (
              <div key={label} className="bg-zinc-900 px-4 py-3 text-center" data-testid={testid}>
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-1">{label}</div>
                <div className={`text-sm font-mono font-semibold ${color}`}>{value ?? "—"}</div>
              </div>
            ))}
          </div>

          {/* RU-PNLTAB: segment → type → symbol → trades for this day, in the calendar's mode */}
          <div className="px-5 pt-4" data-testid="day-breakdown">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">By segment ({mode})</div>
            <PnLBreakdownTree mode={mode} from={date} to={date} compact />
          </div>

          {/* Positions */}
          {detail.by_strategy.length === 0 && detail.closed_trades.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-zinc-600">
              <div className="text-sm">No positions recorded for {date}</div>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {detail.by_strategy.length > 0 ? (
                detail.by_strategy.map((strat) => (
                  <div key={strat.name}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                        {strat.name}
                      </span>
                      <span className={`text-xs font-mono font-semibold ${
                        strat.total_pnl > 0 ? "text-emerald-400" : strat.total_pnl < 0 ? "text-red-400" : "text-zinc-400"
                      }`}>
                        {fmt(strat.total_pnl)}
                      </span>
                    </div>
                    <div className="bg-zinc-800/40 rounded-sm px-4">
                      {strat.trades.map((t, i) => (
                        <TradeRow key={`${t.symbol}-${t.exit_at}-${i}`} trade={t} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-zinc-800/40 rounded-sm px-4">
                  {detail.closed_trades.map((t, i) => (
                    <TradeRow key={`${t.symbol}-${t.exit_at}-${i}`} trade={t} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !detail && (
        <div className="flex items-center justify-center py-10 text-zinc-600 text-sm">
          Failed to load positions
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PnLCalendar() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [mode,  setMode]  = useState("paper");
  const [data,  setData]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(API("/analytics/equity?days=365"), { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.points) setData(d.points); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    data.forEach((p) => {
      const pnl =
        mode === "paper"    ? p.paper_pnl  :
        mode === "live"     ? p.live_pnl   :
        p.daily_pnl;
      map[p.date] = pnl ?? null;
    });
    return map;
  }, [data, mode]);

  // Compute daily P&L % from equity snapshots: pct = pnl / (equity - pnl) * 100
  const byDatePct = useMemo(() => {
    const map = {};
    data.forEach((p) => {
      const pnl = mode === "paper" ? p.paper_pnl : mode === "live" ? p.live_pnl : p.daily_pnl;
      if (pnl != null && p.equity != null) {
        const prevEquity = p.equity - (p.daily_pnl ?? 0);
        map[p.date] = prevEquity > 0 ? (pnl / prevEquity) * 100 : null;
      }
    });
    return map;
  }, [data, mode]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const cells = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const monthStats = useMemo(() => {
    const prefix = `${year}-${String(month + 1).padStart(2, "0")}`;
    const days = Object.entries(byDate)
      .filter(([d]) => d.startsWith(prefix))
      .map(([, v]) => v)
      .filter((v) => v != null);
    const total   = days.reduce((s, v) => s + v, 0);
    const wins    = days.filter((v) => v > 0).length;
    const losses  = days.filter((v) => v < 0).length;
    const best    = days.length ? Math.max(...days) : null;
    const worst   = days.length ? Math.min(...days) : null;
    const realized = data
      .filter((p) => p.date?.startsWith(prefix) && p.paper_pnl != null)
      .reduce((s, p) => s + p.paper_pnl, 0);
    return { total, wins, losses, best, worst, realized };
  }, [byDate, data, year, month]);

  const realizedByDate = useMemo(() => {
    const map = {};
    data.forEach((p) => { map[p.date] = p.paper_pnl ?? null; });
    return map;
  }, [data]);

  const yearDays = useMemo(() => {
    const arr = [];
    const start = new Date(year, 0, 1);
    const end   = new Date(year, 11, 31);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10);
      arr.push({ date: key, pnl: byDate[key] ?? null, day: d.getDay() });
    }
    return arr;
  }, [byDate, year]);

  const maxAbsYear = useMemo(() => {
    const vals = yearDays.map((d) => d.pnl).filter((v) => v != null).map(Math.abs);
    return vals.length ? Math.max(...vals) : 1;
  }, [yearDays]);

  const isFuture = (d) => {
    const dt = new Date(year, month, d);
    const t  = new Date(); t.setHours(0,0,0,0);
    return dt > t;
  };

  const handleDayClick = useCallback((dateStr, pnl, future) => {
    if (future) return;
    setSelectedDate(dateStr);
  }, []);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <PageHeader
          overline="Analytics"
          title="P&L Calendar"
          description="Daily profit & loss across trading sessions"
        />

        {/* Mode selector */}
        <div className="flex items-center gap-2">
          {["paper","live","combined"].map((m) => (
            <button
              key={m}
              data-testid={`cal-mode-${m}`}
              onClick={() => setMode(m)}
              className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                mode === m
                  ? "bg-indigo-600 border-indigo-500 text-white"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
              }`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>

        <div className="text-[10px] text-zinc-500" data-testid="mode-note">
          {mode === "combined"
            ? `Combined shows the ${COMBINED_LABEL.charAt(0).toLowerCase() + COMBINED_LABEL.slice(1)}, with the realized P&L of closed trades alongside. All figures ${BEFORE_CHARGES}.`
            : `All figures ${BEFORE_CHARGES} (brokerage and taxes not deducted).`}
        </div>

        {/* Monthly summary */}
        <div className={`grid grid-cols-2 ${mode === "combined" ? "sm:grid-cols-6" : "sm:grid-cols-5"} gap-3`}>
          {[
            { label: `${mode === "combined" ? `Month ${COMBINED_LABEL}` : "Month P&L"} · ${BEFORE_CHARGES}`, value: fmt(monthStats.total), color: monthStats.total >= 0 ? "text-emerald-400" : "text-red-400", testid: "month-total" },
            ...(mode === "combined"
              ? [{ label: `Month ${REALIZED_LABEL} · ${BEFORE_CHARGES}`, value: fmt(monthStats.realized), color: monthStats.realized >= 0 ? "text-emerald-400" : "text-red-400", testid: "month-realized" }]
              : []),
            { label: "Winning Days", value: monthStats.wins,  color: "text-emerald-400" },
            { label: "Losing Days",  value: monthStats.losses, color: "text-red-400" },
            { label: "Best Day",     value: fmt(monthStats.best),  color: "text-emerald-400" },
            { label: "Worst Day",    value: fmt(monthStats.worst), color: "text-red-400" },
          ].map(({ label, value, color, testid }) => (
            <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid={testid}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
              <div className={`text-lg font-mono-data font-semibold ${color}`}>{value ?? "—"}</div>
            </div>
          ))}
        </div>

        {/* Calendar */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-zinc-100">
              {MONTH_NAMES[month]} {year}
            </span>
            <button
              onClick={nextMonth}
              disabled={year === today.getFullYear() && month >= today.getMonth()}
              className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 border-b border-zinc-800">
            {DAY_LABELS.map((d) => (
              <div key={d} className="text-center text-[10px] uppercase tracking-widest text-zinc-600 py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-48 text-zinc-600 text-sm animate-pulse">
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {cells.map((day, i) => {
                if (day === null) {
                  return <div key={`pad-${i}`} className="border-b border-r border-zinc-800/40 min-h-[80px]" />;
                }
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const pnl = byDate[dateStr] ?? null;
                const future = isFuture(day);
                const isToday = dateStr === today.toISOString().slice(0, 10);
                const isSelected = dateStr === selectedDate;
                const { bg, text, border } = dayColor(future ? null : pnl);
                const col = i % 7;
                const borderR = col < 6 ? "border-r" : "";
                const clickable = !future;

                return (
                  <div
                    key={dateStr}
                    data-testid={`cal-day-${dateStr}`}
                    onClick={() => handleDayClick(dateStr, pnl, future)}
                    className={`${bg} ${border} border-b ${borderR} min-h-[80px] p-2 flex flex-col justify-between transition-colors
                      ${clickable ? "cursor-pointer hover:brightness-125" : ""}
                      ${isSelected ? "ring-2 ring-inset ring-indigo-500" : ""}
                    `}
                  >
                    <span className={`text-xs font-medium ${isToday ? "text-indigo-400 font-bold" : "text-zinc-500"}`}>
                      {day}
                    </span>
                    {!future && pnl != null && (
                      <div className="mt-1">
                        <span className={`text-xs font-mono-data font-semibold ${text} block leading-tight`}
                              data-testid={`cell-pnl-${dateStr}`}
                              title={`${mode === "combined" ? COMBINED_LABEL : mode === "live" ? "Live P&L" : "Realized P&L"}, ${BEFORE_CHARGES}`}>
                          {pnl >= 0 ? "+" : ""}₹{Math.abs(pnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </span>
                        {byDatePct[dateStr] != null && (
                          <span className={`text-[9px] font-mono ${text} opacity-70 block`}>
                            {byDatePct[dateStr] >= 0 ? "+" : ""}{byDatePct[dateStr].toFixed(2)}%
                          </span>
                        )}
                        {mode === "combined" && realizedByDate[dateStr] != null && (
                          <span className="text-[9px] font-mono text-zinc-400 block" data-testid={`cell-realized-${dateStr}`}
                                title={`${REALIZED_LABEL} (closed trades, ${BEFORE_CHARGES})`}>
                            {REALIZED_LABEL} {fmt(realizedByDate[dateStr])}
                          </span>
                        )}
                        {pnl > 0 && <TrendingUp className={`w-3 h-3 ${text} mt-0.5 opacity-70`} />}
                        {pnl < 0 && <TrendingDown className={`w-3 h-3 ${text} mt-0.5 opacity-70`} />}
                        {pnl === 0 && <Minus className={`w-3 h-3 ${text} mt-0.5 opacity-70`} />}
                      </div>
                    )}
                    {!future && pnl == null && (
                      <span className="text-[10px] text-zinc-700">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Day detail — inline below calendar */}
        {selectedDate && (
          <DayDetail date={selectedDate} mode={mode} onClose={() => setSelectedDate(null)} />
        )}

        {/* Year heatmap */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">{year} — Daily P&L Heatmap</div>
          <div className="flex flex-wrap gap-[3px]">
            {yearDays.map(({ date, pnl, day: dow }) => {
              const alpha = pnl != null ? 0.2 + 0.8 * intensity(pnl, maxAbsYear) : 0;
              const color =
                pnl == null ? "bg-zinc-800"
                : pnl > 0   ? "bg-emerald-500"
                :              "bg-red-500";
              return (
                <div
                  key={date}
                  title={`${date}: ${pnl != null ? fmt(pnl) : "no data"}${mode === "combined" ? ` (${COMBINED_LABEL})` : ""}`}
                  onClick={() => {
                    const dt = new Date(date);
                    setYear(dt.getFullYear());
                    setMonth(dt.getMonth());
                    const today0 = new Date(); today0.setHours(0,0,0,0);
                    if (new Date(date) <= today0) setSelectedDate(date);
                  }}
                  className={`w-3 h-3 rounded-[2px] cursor-pointer ${color} transition-opacity hover:opacity-100`}
                  style={{ opacity: pnl != null ? alpha : 0.15 }}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-zinc-600">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-[2px] bg-emerald-500 opacity-80" /> Profit
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-[2px] bg-red-500 opacity-80" /> Loss
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-[2px] bg-zinc-700" /> No data
            </div>
            <span className="ml-auto">Click a cell to see day detail</span>
          </div>
        </div>
      </div>

    </AppShell>
  );
}
